import React, { useState } from "react";
import Highlighter from "react-highlight-words";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { axios } from "../../../../../../Global";
import { useDispatch } from "react-redux";
import { setAppError } from "../../../../../../store/userSlice";
import PromptElement from "../../../../PromptElement";
import JEAssignPopup from "./JEAssign";
import ConvertJobOrderPopup from "./ConvertJobOrderPopup";
import RecAgencyDownloadPupup from "./RecAgencyDownloadPopup";
import { InitialSelectedTItles } from "../JobEnquiry/ListDataProjectJobEnquiry";
import sectionTableReport from "../excel/SectionTableReport";
import SearchFilter from "../../../../../../components/SmartDataTable/SearchFilter";
import PaginationControls from "../../../../../../components/SmartDataTable/PaginationControls";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function ListDataRecAgency({
  recAgencyData,
  titles,
  loading,
  setShowAddNewAgencyPopUp,
  setAgencyData,
}) {
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchWords, setSearchWords] = useState([]);

  const [openCostSheetPopup, setOpenCostSheetPopup] = useState({ open: false, recAgencyId: null, jobEnquiryId: null });
  const [JEAssignState, setJEAssignState] = useState({ open: false, data: null });
  const [showPromptElement, setShowPromptElement] = useState({ open: false, id: null });
  const [downloadPopupState, setDownloadPopupState] = useState({ open: false, rowId: null });

  const HandleUpdateAcceptanceStatus = async (value, id) => {
    try {
      const res = await axios.put(
        `/dashboard/project-recruitment-agency-job-enquiry-status-update/${id}`,
        { job_enquiry_acceptance_status: value, type: "JOB ENQUIRY ACCEPTANCE STATUS" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 200) {
        setAgencyData(res.data.data);
        dispatch(setAppError({ msg: res.data.message, color: "success" }));
      }
    } catch (error) {
      dispatch(setAppError({ msg: error?.response?.data?.message || error?.message }));
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(
        `/dashboard/project-recruitment-agency-job-enquiry-delete/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 200) {
        setAgencyData(res.data.data);
        dispatch(setAppError({ msg: res.data.message, color: "success" }));
      }
    } catch (error) {
      dispatch(setAppError({ msg: error?.response?.data?.message || error?.message }));
    }
  };

  const allData = recAgencyData?.recruitment_agency || [];

  const filtered = searchTerm.trim()
    ? allData.filter((item) =>
        ["job_enquiry_number", "job_enquiry_status", "job_order_status"].some((k) =>
          String(item[k] ?? "").toLowerCase().includes(searchTerm.toLowerCase())
        ) || String(item?.vendor?.name ?? "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allData;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageData = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    setSearchWords(val ? val.trim().split(/\s+/) : []);
    setCurrentPage(1);
  };

  const reportHeadings = [
    { title: "REC. AGENCY NAME", key: "vendor.name", nested: true },
    { title: "REC. AGENCY JOB ENQ NO", key: "job_enquiry_number" },
    { title: "NATIONALITY", key: "vendor.nationality_id", type: "array", array: recAgencyData?.nationality, arrayKey: "name", nested: true },
    { title: "JOB ENQUIRY STATUS", key: "job_enquiry_status" },
    { title: "JOB ENQUIRY ACCEPTANCE STATUS", key: "job_enquiry_acceptance_status" },
    { title: "JOB ORDER STATUS", key: "job_order_status" },
  ];

  return (
    <>
      {JEAssignState.open && <JEAssignPopup state={JEAssignState} setState={setJEAssignState} recAgencyData={recAgencyData} setAgencyData={setAgencyData} />}
      {showPromptElement?.open && <PromptElement showPromptElement={showPromptElement} setShowPromptElement={setShowPromptElement} deleteHandler={handleDelete} />}
      {openCostSheetPopup.open && <ConvertJobOrderPopup showPromptElement={openCostSheetPopup} setShowPromptElement={setOpenCostSheetPopup} setAgencyData={setAgencyData} />}
      {downloadPopupState.open && <RecAgencyDownloadPupup InitialSelectedTItles={InitialSelectedTItles} openGenerateQuotation={downloadPopupState} setOpenGenerateQuotation={setDownloadPopupState} />}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center w-full mb-4 flex-row-reverse">
        <SearchFilter searchTerm={searchTerm} onSearchChange={handleSearchChange} placeholder="Search across all columns..." />
        <div className="flex gap-2 items-center">
          <button className="p-2 px-4 rounded-md bg-blue disabled:bg-gray text-white min-w-fit text-sm" onClick={() => sectionTableReport(filtered, reportHeadings, "Rec Agency Report")}>
            Download
          </button>
          <button type="button" className="p-2 px-4 rounded-md bg-red text-white min-w-fit text-sm" onClick={() => setShowAddNewAgencyPopUp(true)}>
            + Add New Rec. Agency
          </button>
        </div>
      </div>

      <p className="text-sm w-full text-right font-medium mb-1 xl:hidden block">{`Slide / scroll to right >`}</p>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]">
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
            <tr className="bg-gray-50">
              {titles?.map((title, i) => (
                <th key={i} className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider bg-gray-50" style={{ backgroundColor: '#f9fafb' }}>
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {loading ? (
              <tr>
                <td colSpan={titles?.length || 8} className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center">
                    <span className="loading loading-spinner loading-md mr-2"></span>
                    Loading data...
                  </div>
                </td>
              </tr>
            ) : pageData.length === 0 ? (
              <tr><td colSpan={titles?.length || 8} className="px-6 py-8 text-center text-gray-500">{searchTerm ? `No results found for "${searchTerm}"` : "No Rec Agency added."}</td></tr>
            ) : (
              pageData.map((list, index) => (
                <tr key={list.id ?? index} className="border-b-[1.5px] border-slate-100 hover:bg-gray-50">
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{(safePage - 1) * pageSize + index + 1}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                    <Highlighter searchWords={searchWords} autoEscape textToHighlight={list?.vendor?.name || "-"} />
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                    <Highlighter searchWords={searchWords} autoEscape textToHighlight={list?.job_enquiry_number || "-"} />
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                    <Highlighter searchWords={searchWords} autoEscape textToHighlight={recAgencyData?.nationality?.find((c) => c.id === list?.vendor?.nationality_id)?.name || "-"} />
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base">
                    <span className={`font-medium ${list?.job_enquiry_status === "SENT" ? "text-green-700" : "text-red"}`}>
                      <Highlighter searchWords={searchWords} autoEscape textToHighlight={list?.job_enquiry_status || "-"} />
                    </span>
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                    <select value={list?.job_enquiry_acceptance_status} onChange={(e) => HandleUpdateAcceptanceStatus(e.target.value, list?.id)} className="select select-sm select-bordered w-full min-w-[10rem]">
                      <option value="ACCEPTED">ACCEPTED</option>
                      <option value="NOT ACCEPTED">NOT ACCEPTED</option>
                    </select>
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base">
                    <span className={`font-medium ${list?.job_order_status === "SENT" ? "text-green-700" : "text-red"}`}>
                      <Highlighter searchWords={searchWords} autoEscape textToHighlight={list?.job_order_status || "-"} />
                    </span>
                  </td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                    <div className="relative">
                      <button className="p-1 px-2 rounded-md ring-1 ring-slate-600 peer"><BiDotsVerticalRounded size="1.4rem" /></button>
                      <div className="hidden absolute right-0 top-full w-48 bg-white shadow-xl rounded-lg pt-2 px-2 pb-2 ring-1 ring-slate-200 peer-hover:flex hover:flex flex-col gap-1 z-50">
                        {list?.job_enquiry_status !== "SENT" && (
                          <button onClick={() => setJEAssignState({ open: true, data: list })} className="p-2 w-full text-left hover:bg-red hover:text-white rounded-md text-xs">Assign JE</button>
                        )}
                        {list?.job_enquiry_status === "SENT" && (
                          <button onClick={() => setDownloadPopupState({ open: true, rowId: list?.project_job_enquiry_id })} className="p-2 w-full text-left hover:bg-red hover:text-white rounded-md text-xs">Download</button>
                        )}
                        <button onClick={() => setShowPromptElement({ open: true, id: list?.id })} className="p-2 w-full text-left hover:bg-red hover:text-white rounded-md text-xs text-red">Delete</button>
                        {list?.job_enquiry_status === "SENT" && list?.job_enquiry_acceptance_status === "ACCEPTED" && list?.project_job_order == null && (
                          <button onClick={() => setOpenCostSheetPopup({ open: true, recAgencyId: list?.id, jobEnquiryId: list?.project_job_enquiry_id })} className="p-2 w-full text-left hover:bg-red hover:text-white rounded-md text-xs">Convert to Job Order</button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls currentPage={safePage} totalPages={totalPages} pageSize={pageSize} totalRecords={filtered.length} pageSizeOptions={PAGE_SIZE_OPTIONS} onPageChange={(p) => setCurrentPage(p)} onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }} isLoading={loading} />
    </>
  );
}
